import { convertLinear, convertArea } from "@/components/controller/project-detail/takeoff-helpers";

export interface RawMeasurement {
  annotation_type: string;
  measured_value: number | null;
  unit: string;
  takeoff_group_id?: string | null;
}

export interface AggregatedTotals {
  totalLinear: number;
  totalArea: number;
  totalCount: number;
}

/** Extended annotation shape for drilldown / traceability UI */
export interface DrilldownAnnotation {
  id: string;
  label: string | null;
  color: string;
  page_number: number;
  created_at: string;
  annotation_type: string;
  measured_value: number | null;
  unit: string;
  takeoff_group_id: string | null;
  source_estimate_annotation_id: string | null;
  /** The FK column value — project_document_id or estimate_document_id */
  document_id: string;
}

export interface DocumentSubtotal {
  documentId: string;
  documentName: string;
  totals: AggregatedTotals;
  annotationCount: number;
  pagesTouched: Set<number>;
  annotations: DrilldownAnnotation[];
}

/** Shared mixed-unit aggregation for workspace, groups, and unassigned buckets */
export function aggregateMeasurements(
  annotations: RawMeasurement[],
  displayUnit: string
): AggregatedTotals {
  let totalLinear = 0;
  let totalArea = 0;
  let totalCount = 0;

  annotations.forEach((ann) => {
    const val = ann.measured_value ?? 0;
    if (ann.annotation_type === "measurement_linear" && val > 0) {
      totalLinear += convertLinear(val, ann.unit || "ft", displayUnit);
    } else if (ann.annotation_type === "measurement_area" && val > 0) {
      totalArea += convertArea(val, ann.unit || "ft", displayUnit);
    } else if (ann.annotation_type === "measurement_count") {
      totalCount += val > 0 ? val : 1;
    }
  });

  return { totalLinear, totalArea, totalCount };
}

/** Filter annotations by group and aggregate */
export function aggregateForGroup(
  annotations: RawMeasurement[],
  groupId: string | null,
  displayUnit: string
): AggregatedTotals {
  const filtered = annotations.filter((a) =>
    groupId === null ? !a.takeoff_group_id : a.takeoff_group_id === groupId
  );
  return aggregateMeasurements(filtered, displayUnit);
}

/** Group drilldown annotations by document and return per-document subtotals */
export function aggregateByDocument(
  annotations: DrilldownAnnotation[],
  displayUnit: string,
  documentMap: Record<string, string>
): DocumentSubtotal[] {
  const byDoc = new Map<string, DrilldownAnnotation[]>();

  annotations.forEach((ann) => {
    const docId = ann.document_id;
    if (!byDoc.has(docId)) byDoc.set(docId, []);
    byDoc.get(docId)!.push(ann);
  });

  const result: DocumentSubtotal[] = [];
  byDoc.forEach((anns, docId) => {
    const pages = new Set<number>();
    anns.forEach((a) => pages.add(a.page_number));

    result.push({
      documentId: docId,
      documentName: documentMap[docId] || "Unknown Document",
      totals: aggregateMeasurements(anns, displayUnit),
      annotationCount: anns.length,
      pagesTouched: pages,
      annotations: anns,
    });
  });

  // Sort by annotation count descending
  result.sort((a, b) => b.annotationCount - a.annotationCount);
  return result;
}
